import React, { useEffect, useMemo, useRef, useState } from "react";
import { Steps } from "intro.js-react";
import "intro.js/introjs.css";
import "./styles.scss";
import useTour from "./hooks/useTour";
import { tours } from "./tours/tours";
import Splashscreen from "./components/Splashscreen";
import { callActionsForNextTourStep } from "./helpers/callActionsForNextTourStep";
import { setIsTourAlreadyShown } from "./helpers/setIsTourAlreadyShown";
import { useAppSelector } from "../../store/hooks";
import { selectDirection, selectTranslatedString } from "../../store/selectors/appSelectors";
import { selectCurrentUser } from "../../store/selectors/authSelectors";
import { useTranslate } from "../../utils/hooks/useTranslate";
import { ProductTourActionsE } from "../../models/enums";
import { useNavigate } from "react-router-dom";
import { RoutesE } from "../../routes/routesData";

// While present on <body>, intro.js' tooltip + spotlight are hidden (see styles.scss). We keep it on
// from the moment <Steps> mounts until the first step has been measured and re-aligned, so step 1's
// stray "floating top-left" frame — which reads as a duplicate slide — is never painted.
const TOUR_POSITIONING_CLASS = "tour-positioning";

/**
 * How to create a new tour:
 *
 * 1. Add new data (CSS selectors and events if it is necessary) inside ./tours/tours.js
 * 2. (optional) Create [tourName]Tour.scss file within ./tours directory, write styles for each step
 *    and import it to the ./styles.scss
 * 3. Add <ProductTourWrapper/> component where do you want
 *
 *  Step title text key example (use this key on the server side):
 *  0_tour_inbox_title
 *
 *  Step description text key example (use this key on the server side):
 *  0_tour_inbox_text
 *
 *  Step tooltip element CSS className example:
 *  .tooltip_0_tour_inbox or .tooltip_1_tour_inbox_rtl
 *
 *  Step highlight element CSS className example:
 *  .highlight_0_tour_inbox or .highlight_0_tour_inbox_rtl
 */

const ProductTour = ({ tourName, tourID, setIsShownFirstTime, withSplashScreen, initialStep }) => {
  const direction = useAppSelector(selectDirection);
  const user: any = useAppSelector(selectCurrentUser);
  const translatedStrings = useAppSelector(selectTranslatedString);
  const stepsRef = useRef(null);
  const { t } = useTranslate();
  const navigate = useNavigate();

  useEffect(() => {
    document.addEventListener(ProductTourActionsE.NAVIGATE_TO_DELIVERY, handleNavigateToDelivery);
    return () => {
      document.removeEventListener(ProductTourActionsE.NAVIGATE_TO_DELIVERY, handleNavigateToDelivery);
    }
  }, []);

  const handleNavigateToDelivery = () => {
    navigate(RoutesE.DELIVERIES);
  };

  // Make steps from CSS selectors
  // Memoize so the selectors array (and therefore the steps array) keeps a stable identity across
  // re-renders — see the note in useTour about why identity churn re-starts the tour.
  const tourElementSelectors = useMemo(
    () => (tours[tourName] ? Object.keys(tours[tourName]) : []),
    [tourName]
  );
  const steps = useTour(tourName, tourElementSelectors);

  // Check if "Take a tour" button clicked
  const [isTourStarted, setIsTourStarted] = useState(false);

  // The intro.js <Steps> is mounted once the splash is dismissed (or immediately when there is no
  // splash), but only after one animation frame: mounting it in the same tick the splash closes lets
  // intro.js measure the first target before the page underneath has settled, so step 1 paints at the
  // wrong (floating, top-left) position and then snaps into place — looking like a stray duplicate
  // slide. Waiting a frame lets layout settle first.
  //
  // intro.js-react also calls introJs.exit() from componentWillUnmount, which fires our onExit. Under
  // React 18 StrictMode (and on hot-reload) the tour is mounted, unmounted and remounted, so that
  // spurious exit() would write the "already shown" flag and hide the tour the instant it appears
  // (step 1 flashes and is gone). Ignore any exit that arrives before the tour has settled: arm a ref
  // a tick after Steps mounts, and only honour real exits.
  const isShowingSteps = isTourStarted || !withSplashScreen;
  const [areStepsMounted, setAreStepsMounted] = useState(false);
  const isTourReadyRef = useRef(false);
  // Guards the one-time reveal: flips true once the first step is aligned, so later step changes
  // (which never flash) don't re-toggle the hide class.
  const firstStepRevealedRef = useRef(false);

  const revealTour = () => {
    if (firstStepRevealedRef.current) return;
    firstStepRevealedRef.current = true;
    document.body.classList.remove(TOUR_POSITIONING_CLASS);
  };

  // Under React StrictMode the intro.js-react <Steps> child mounts → unmounts → remounts, and the
  // first instance's exit() is async, so a SECOND intro.js instance gets created and an extra set of
  // layers is left in the DOM. The result is a duplicate, frozen "1 / 7" tooltip shown before the
  // real step 1.
  //
  // intro.js drives every step via `document.querySelector(".introjs-tooltipReferenceLayer")` — a
  // global, FIRST-match lookup — so whichever layer is first in the DOM is the live one that gets
  // positioned and advances through the steps; any later duplicate is an inert, frozen phantom.
  // Therefore keep the FIRST layer of each kind and drop the rest (the phantoms).
  const removeOrphanIntroLayers = () => {
    [
      ".introjs-overlay",
      ".introjs-helperLayer",
      ".introjs-tooltipReferenceLayer",
      ".introjs-disableInteraction",
    ].forEach((selector) => {
      const nodes = document.querySelectorAll(selector);
      for (let i = 1; i < nodes.length; i += 1) nodes[i].remove();
    });
  };

  useEffect(() => {
    if (!isShowingSteps) {
      setAreStepsMounted(false);
      isTourReadyRef.current = false;
      return;
    }
    // Hide intro.js' layers until step 1 has settled so its misplaced first frame is never shown.
    firstStepRevealedRef.current = false;
    document.body.classList.add(TOUR_POSITIONING_CLASS);
    let armId;
    let revealRaf;
    const frameId = requestAnimationFrame(() => {
      setAreStepsMounted(true);
      armId = setTimeout(() => {
        isTourReadyRef.current = true;
      }, 0);

      // intro.js places step 1 before the page has settled (tooltip lands misplaced, far left) and
      // then snaps it beside the target — that stray first frame reads as a duplicate slide. We can't
      // reveal on a fixed timer (it can land mid-snap) nor from onAfterStepChange (intro fires
      // onafterchange for the initial step synchronously inside its componentDidMount, before React
      // has assigned stepsRef.current, so that path bails on step 1). Instead force one refresh, then
      // poll the tooltip's on-screen position and only reveal once it has stopped moving across a few
      // frames — with a ~1s fallback so the tour can never stay hidden.
      let attempts = 0;
      let stableFrames = 0;
      let lastPos = "";
      const revealWhenStable = () => {
        attempts += 1;
        // Drop any phantom duplicate layers every frame so the stray box never survives to reveal.
        removeOrphanIntroLayers();
        if (attempts === 1) {
          ensureStepVisible(0);
          refreshTourLayers();
        }
        const tooltips = document.querySelectorAll(".introjs-tooltip");
        // The live tooltip is the first in the DOM (see removeOrphanIntroLayers).
        const tooltip = tooltips[0];
        const rect = tooltip ? tooltip.getBoundingClientRect() : null;
        const pos = rect ? `${Math.round(rect.left)}x${Math.round(rect.top)}` : "";
        // Only one tooltip must remain, and its position must have settled, before we reveal.
        const isSingle = tooltips.length <= 1;
        if (pos && pos === lastPos) {
          stableFrames += 1;
        } else {
          stableFrames = 0;
          lastPos = pos;
        }
        if ((pos && isSingle && stableFrames >= 3) || attempts >= 90) {
          revealTour();
          return;
        }
        revealRaf = requestAnimationFrame(revealWhenStable);
      };
      revealRaf = requestAnimationFrame(revealWhenStable);
    });
    return () => {
      cancelAnimationFrame(frameId);
      if (armId) clearTimeout(armId);
      if (revealRaf) cancelAnimationFrame(revealRaf);
      isTourReadyRef.current = false;
      document.body.classList.remove(TOUR_POSITIONING_CLASS);
    };
  }, [isShowingSteps]);

  // Since the grid refactor the page content (#app-scroll), not the window, is the scroll container.
  // intro.js v6 auto-scrolls with window.scrollBy / measures against window.innerHeight, so it can no
  // longer bring a step's target into view or keep the highlight aligned. We own scrolling instead:
  // scroll each in-container target into the middle of #app-scroll, then refresh intro.js' layers so
  // the spotlight + tooltip line up. Header/sidebar targets (outside #app-scroll, always visible) are
  // left untouched so we don't scroll the list for elements that don't move with it.
  const ensureStepVisible = (stepIndex) => {
    const selector = steps[stepIndex]?.element;
    if (!selector) return;
    const element = document.querySelector(selector);
    const scroller = document.getElementById("app-scroll");

    if (element && scroller && scroller.contains(element)) {
      const elementRect = element.getBoundingClientRect();
      const scrollerRect = scroller.getBoundingClientRect();
      const isFullyVisible =
        elementRect.top >= scrollerRect.top &&
        elementRect.bottom <= scrollerRect.bottom;

      if (!isFullyVisible) {
        element.scrollIntoView({ block: "center", inline: "nearest" });
      }
    }
  };

  const refreshTourLayers = () => {
    // Re-measure the highlight/tooltip after a scroll or page change so focus tracks the element.
    // @ts-ignore - introJs instance is exposed by intro.js-react's Steps ref
    stepsRef.current?.introJs?.refresh?.();
  };

  // intro.js tour preset. Memoized so its identity is stable across re-renders; otherwise a new
  // options object each render makes intro.js-react's componentDidUpdate reconfigure/re-render the
  // tour every time, which is one of the ways a duplicate step-1 tooltip appears. Depends on the
  // translations store (for the t`...` labels) and direction, not on the unstable `t` function.
  const options = useMemo(
    () => ({
      showStepNumbers: true,
      // Renders intro.js' own per-step counter as "N / M" (e.g. "1 / 7"). Using the native counter
      // means every tooltip shows its own true step number — including the initial step (which the
      // old data-step-nums hook never set) and any stray/phantom tooltip — a UX aid too.
      stepNumbersOfLabel: "/",
      nextLabel: t`tour next`,
      prevLabel: t`tour prev`,
      doneLabel: t`tour done`,
      hidePrev: true,
      disableInteraction: true,
      exitOnOverlayClick: false,
      exitOnEsc: true,
      keyboardNavigation: true,
      // We scroll the target ourselves (see ensureStepVisible); intro.js' own window-based scroll is
      // a no-op against the inner #app-scroll container, so turn it off to avoid a competing jump.
      scrollToElement: false,
      highlightClass: `highlight-element-${direction}`,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [direction, translatedStrings]
  );

  // Do some actions before next step will occur
  const onBeforeStepChange = (nextStepIndex, nextElement) => {
    stepsRef.current?.updateStepElement(nextStepIndex);
    callActionsForNextTourStep(nextStepIndex, stepsRef, tourName);
    // Bring the upcoming target into view before intro.js positions its spotlight on it.
    ensureStepVisible(nextStepIndex);
  };

  // After the step is shown, re-align the spotlight. Some steps navigate to another page first
  // (e.g. clicking #hidden-span → /deliveries), so the target either isn't mounted yet or is the OLD
  // page's element that is about to unmount. Positioning once against that transient element leaves
  // the tooltip floating. Instead we keep re-aligning each frame until the target's on-screen box has
  // held still for a few frames (the new page has settled), with a ~2s budget. Bail if the user moved
  // to another step so we never fight an in-progress transition.
  const onAfterStepChange = (currentStepIndex) => {
    let attempts = 0;
    let stableFrames = 0;
    let lastBox = "";
    const align = () => {
      attempts += 1;
      // @ts-ignore - introJs instance is exposed by intro.js-react's Steps ref
      if (stepsRef.current?.introJs?._currentStep !== currentStepIndex) {
        revealTour();
        return;
      }

      const selector = steps[currentStepIndex]?.element;
      const element = selector && document.querySelector(selector);

      if (element) {
        ensureStepVisible(currentStepIndex);
        // Re-point intro.js at the element currently in the DOM before re-measuring. onBeforeStepChange
        // captured the element on the OLD page (before #hidden-span navigated to /deliveries), so
        // intro's stored _introItems[i].element is now detached and refresh() would position the
        // tooltip against a stale node (floating top-left). updateStepElement re-queries the selector.
        stepsRef.current?.updateStepElement(currentStepIndex);
        refreshTourLayers();
        revealTour();
        const rect = element.getBoundingClientRect();
        const box = `${Math.round(rect.left)}x${Math.round(rect.top)}x${Math.round(rect.width)}`;
        if (box === lastBox) {
          stableFrames += 1;
        } else {
          stableFrames = 0;
          lastBox = box;
        }
        // Target has settled in place — stop re-aligning.
        if (stableFrames >= 4) return;
      } else {
        // Target gone/not mounted yet (mid-navigation) — keep waiting for the new page's element.
        stableFrames = 0;
        lastBox = "";
      }

      if (attempts < 120) {
        requestAnimationFrame(align);
      } else {
        // Budget exhausted (e.g. #create-delivery-button never appears on an empty list) — reveal so
        // the tour is never stuck hidden, and leave intro.js' own placement alone.
        revealTour();
      }
    };

    align();
  };

  // Finish tour if something went wrong
  const onPreventChange = () => {
    if (!isTourReadyRef.current) return;
    if (tourName !== "inbox") {
      setIsTourAlreadyShown(tourID);
    }
    setIsShownFirstTime(false);
  };

  const exitTour = () => {
    // Ignore the StrictMode/hot-reload remount exit (see isTourReadyRef above) — only a real exit
    // (the user pressing the X / Esc, or finishing the last step) should dismiss the tour.
    if (!isTourReadyRef.current) return;
    if (tourName !== "inbox") {
      setIsTourAlreadyShown(tourID);
    }
    setIsShownFirstTime(false);
  };

  const skipTour = (e) => {
    e.preventDefault();

    setIsTourAlreadyShown(tourID);
    setIsShownFirstTime(false);
  };

  const startTour = () => {
    if (tourName.includes('campaigns')) {
      localStorage.removeItem(`tour-deliveries-${user.userId}`);
    }
    setIsTourAlreadyShown(tourID);
    setIsTourStarted(true);
  };

  return (
    <>
      {!!user?.userId &&
        (!isShowingSteps ? (
          <Splashscreen
            tourName={tourName}
            handleStart={startTour}
            handleSkip={skipTour}
          />
        ) : (
          areStepsMounted && (
            <>
              {/*@ts-ignore*/}
              <Steps
                ref={stepsRef}
                enabled
                steps={steps}
                initialStep={initialStep}
                options={options}
                onExit={exitTour}
                onBeforeChange={onBeforeStepChange}
                onAfterChange={onAfterStepChange}
                onPreventChange={onPreventChange}
              />
            </>
          )
        ))}
    </>
  );
};

export default ProductTour;
